import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRMData } from '../context/CRMDataContext';
import SafeIcon from '../common/SafeIcon';
import ProjectCard from '../components/projects/ProjectCard';
import ProjectModal from '../components/projects/ProjectModal';
import FilterPanel from '../components/common/FilterPanel';

const PROJECTS_STORAGE_KEY = 'saleswork_demo_projects';

const loadProjects = () => {
  try {
    const saved = localStorage.getItem(PROJECTS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

const normalizeSupabaseProject = project => ({
  ...project,
  source: 'supabase',
  status: project.status || 'Planlagt',
  projectType: project.project_type,
  projectFor: project.project_for,
  startDate: project.start_date,
  endDate: project.end_date,
  target: project.target_value
});

const markLegacyProject = project => ({
  ...project,
  source: 'localStorage',
  isLegacy: true,
  status: project.status || 'Planlagt'
});

const ProjectList = () => {
  const navigate = useNavigate();
  const { projects } = useCRMData();
  const [localProjects, setLocalProjects] = useState(loadProjects);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const availableFilters = [
    { key: 'name', label: 'Prosjektnavn', type: 'text' },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Planlagt', label: 'Planlagt' },
        { value: 'Aktiv', label: 'Aktiv' },
        { value: 'På pause', label: 'På pause' },
        { value: 'Arkivert', label: 'Arkivert' },
        { value: 'Avsluttet', label: 'Avsluttet' },
        { value: 'Slettet', label: 'Slettet' }
      ]
    }
  ];

  const supabaseProjects = Array.isArray(projects)
    ? projects.map(normalizeSupabaseProject)
    : [];
  const legacyProjects = Array.isArray(localProjects)
    ? localProjects.map(markLegacyProject)
    : [];

  const projectList = [...supabaseProjects, ...legacyProjects].filter((project, index, all) =>
    all.findIndex(item => item.id === project.id) === index
  );

  const filteredProjects = projectList.filter(project => {
    if (activeFilters.status && project.status !== activeFilters.status) return false;
    if (activeFilters.name && !project.name.toLowerCase().includes(activeFilters.name.toLowerCase())) return false;
    return true;
  });

  const saveProject = project => {
    const savedProject = {
      ...project,
      id: project.id || `demo-project-${Date.now()}`,
      created_at: project.created_at || new Date().toISOString()
    };
    const nextProjects = [...localProjects, savedProject];
    setLocalProjects(nextProjects);
    try {
      localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(nextProjects));
    } catch {
      console.warn('[PROJECTS] Could not save demo project locally.');
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen bg-slate-50 space-y-10">
      <div className="flex justify-between items-end pb-8 border-b border-slate-200">
        <div><h1 className="text-4xl font-black text-slate-900">Prosjekter</h1></div>
        <button type="button" onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold">
          <SafeIcon icon="➕" /> Nytt Prosjekt
        </button>
      </div>

      <FilterPanel availableFilters={availableFilters} activeFilters={activeFilters} onFilterChange={setActiveFilters} />

      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {filteredProjects.map(project => (
            <ProjectCard key={project.id} project={project} onClick={() => navigate(`/projects/${project.id}`)} />
          ))}
        </div>
      ) : (
        <div className="text-center py-24">
          <SafeIcon icon="💼" className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-2xl font-black text-slate-900">Ingen prosjekter</h3>
        </div>
      )}

      <ProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={saveProject} />
    </div>
  );
};

export default ProjectList;